import React, { useState, useEffect, useCallback } from 'react';
import { API, ax, btn, inp } from './adminHelpers';

function CustomerRow({ c, toggleVerify }) {
  const [hovered, setHovered] = useState(false);
  const [toggling, setToggling] = useState(false);

  const handleToggle = async () => {
    setToggling(true);
    await toggleVerify(c.id);
    setToggling(false);
  };

  return (
    <tr
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        borderBottom: '1px solid rgba(255,255,255,0.04)',
        background: hovered ? 'rgba(255,255,255,0.02)' : 'transparent',
        transition: 'background 0.2s',
      }}
    >
      {/* Customer ID */}
      <td style={{ padding: '16px 18px', verticalAlign: 'middle' }}>
        <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#fff', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', padding: '4px 8px', borderRadius: 6 }}>
          USR-{String(c.id).padStart(5, '0')}
        </span>
      </td>

      {/* Name */}
      <td style={{ padding: '16px 18px', verticalAlign: 'middle', fontSize: 13, color: '#fff', fontWeight: 600 }}>
        {c.name}
      </td>

      {/* Email */}
      <td style={{ padding: '16px 18px', verticalAlign: 'middle', fontSize: 13, color: '#a0a0b8' }}>
        {c.email}
      </td>

      {/* Bookings Count */}
      <td style={{ padding: '16px 18px', verticalAlign: 'middle', fontSize: 13, color: '#9F5CFF', fontWeight: 700 }}>
        {c.bookingsCount} bookings
      </td>

      {/* Created At */}
      <td style={{ padding: '16px 18px', verticalAlign: 'middle', fontSize: 12, color: '#e0e0e0' }}>
        {new Date(c.created_at).toLocaleString()}
      </td>

      {/* Verified Status */}
      <td style={{ padding: '16px 18px', verticalAlign: 'middle' }}>
        <span style={{
          display: 'inline-block',
          background: c.verified ? 'rgba(39,200,90,0.1)' : 'rgba(245,166,35,0.1)',
          border: `1px solid ${c.verified ? 'rgba(39,200,90,0.3)' : 'rgba(245,166,35,0.3)'}`,
          color: c.verified ? '#27C85A' : '#f5a623',
          fontSize: 10, fontWeight: 700, padding: '4px 10px', borderRadius: 50, textTransform: 'uppercase'
        }}>
          {c.verified ? 'Verified' : 'Unverified'}
        </span>
      </td>

      {/* Actions */}
      <td style={{ padding: '16px 18px', verticalAlign: 'middle' }}>
        <button
          onClick={handleToggle}
          disabled={toggling}
          style={{
            ...btn(c.verified ? '#f5a623' : '#27C85A', true),
            padding: '6px 12px',
            fontSize: 11,
            borderRadius: 8,
            cursor: toggling ? 'not-allowed' : 'pointer',
            opacity: toggling ? 0.6 : 1
          }}
        >
          {toggling ? 'Updating…' : c.verified ? '✕ Unverify' : '✓ Verify'}
        </button>
      </td>
    </tr>
  );
}

export default function CustomersTab() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 15 };
      if (search) params.search = search;

      const r = await ax().get(`${API}/api/admin/customers`, { params });
      setCustomers(r.data.customers || []);
      setTotal(r.data.total || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleVerify = async (id) => {
    try {
      await ax().patch(`${API}/api/admin/customers/${id}/verify`);
      setCustomers(prev => prev.map(c => c.id === id ? { ...c, verified: !c.verified } : c));
    } catch (err) {
      alert(err.response?.data?.error || "Failed to update customer status");
    }
  };

  return (
    <div>
      <h2 style={{ margin: '0 0 20px', fontSize: 18, color: '#fff', fontWeight: 700 }}>👤 Customer Accounts</h2>

      {/* Filter & Search Bar */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: 2, minWidth: 200 }}>
          <label style={{ display: 'block', fontSize: 11, color: '#a0a0b8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 6 }}>Search Customers</label>
          <input
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search by customer name or email…"
            style={inp(false)}
          />
        </div>
        {search && (
          <button
            onClick={() => { setSearch(''); setPage(1); }}
            style={{ ...btn('#555', true), padding: '10px 16px', borderRadius: 10, alignSelf: 'flex-end', height: 40 }}
          >
            Reset
          </button>
        )}
      </div>

      {loading ? (
        <p style={{ color: '#a0a0b8', padding: 20 }}>Loading customers…</p>
      ) : customers.length === 0 ? (
        <p style={{ color: '#a0a0b8', padding: 20 }}>No customers found matching current filters.</p>
      ) : (
        <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 16, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: 900 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.01)' }}>
                  {['Customer ID', 'Name', 'Email', 'Bookings', 'Joined At', 'Status', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '14px 18px', fontSize: 11, fontWeight: 700, color: '#a0a0b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {customers.map(c => (
                  <CustomerRow key={c.id} c={c} toggleVerify={toggleVerify} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination Controls */}
      {total > 15 && (
        <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
          {page > 1 && <button onClick={() => setPage(p => p - 1)} style={btn('#9F5CFF', true)}>← Prev</button>}
          <span style={{ color: '#a0a0b8', fontSize: 13, alignSelf: 'center' }}>Page {page} of {Math.ceil(total / 15)}</span>
          {page * 15 < total && <button onClick={() => setPage(p => p + 1)} style={btn('#9F5CFF', true)}>Next →</button>}
        </div>
      )}
    </div>
  );
}
